// useStagedGuard — the dirty guard of a STAGED draft (a draft that is not a form — an ordered chain, a reorder, a
// terms list, successor picks), the form shell's guard for what has no RHF form (ADR-038 §3). Whatever the container
// (`StagedDrawer` composes it; a dialog holding a draft composes it beside its `Modal`), it owns: every close path
// confirms first while the draft is changed, a busy save closes nothing, a host's own leaving act (Back to a list) is
// wrapped the same way, and while the overlay is open with a changed draft a refresh or a closing tab warns first.
// The draft stays the host's (`dirty` is its word); the host renders `discardDialog` beside its overlay.

import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBeforeUnload } from 'react-router-dom';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';

export interface StagedGuard {
  /** Close the overlay — confirms first while the draft is dirty; nothing while a save is in flight. */
  requestClose: () => void;
  /** Wrap a host's own discarding act (Back to the list) with the same confirm. */
  guarded: (leave: () => void) => () => void;
}

export interface StagedGuardOptions {
  /** The overlay is open — the refresh warning arms only then (a closed overlay may still hold a stale draft). */
  open: boolean;
  /** The host's word: the draft differs from what was read. */
  dirty: boolean;
  /** A save in flight — no close path acts. */
  busy?: boolean;
  onClose: () => void;
  /** The discard dialog's words — the shared "Discard changes?" unless the draft names its own loss. */
  discardTitle?: string;
  discardBody?: string;
}

export function useStagedGuard({ open, dirty, busy = false, onClose, discardTitle, discardBody }: StagedGuardOptions): StagedGuard & {
  discardDialog: ReactNode;
} {
  const { t } = useTranslation('common');
  const [pending, setPending] = useState<(() => void) | null>(null);

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (open && dirty) {
          e.preventDefault();
          e.returnValue = '';
        }
      },
      [open, dirty],
    ),
  );

  const guarded = (leave: () => void) => () => {
    if (busy) return;
    if (dirty) setPending(() => leave);
    else leave();
  };

  const discardDialog = (
    <ConfirmDialog
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        const leave = pending;
        setPending(null);
        leave?.();
      }}
      title={discardTitle ?? t('forms.discardTitle')}
      description={discardBody ?? t('forms.discardBody')}
      confirmLabel={t('forms.discard')}
    />
  );

  return { requestClose: guarded(onClose), guarded, discardDialog };
}
